import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import MapPreviewPage from './components/MapPreviewPage';
import { MapOverlayProvider } from './components/map/mapOverlayStore';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// ── URL routing ────────────────────────────────────────────────────
//
// We don't pull in react-router because the only "routes" we have
// today are:
//
//   - `/map-preview`   → standalone map page (no wallet shell)
//   - `/geoai/:G_PK`   → public profile page (anonymous viewers)
//   - everything else  → full App
//
// Validation for /geoai/ is intentionally cheap — we only confirm the
// shape (Stellar G… 56-char) so the App component can pass it
// straight into ProfileOverlay's `wallet` prop.  The contract-side
// `get_public_profile` will reject malformed addresses anyway, but
// it's nicer to fall back to the regular App for typo'd URLs than to
// leave the visitor staring at a contract error.
const path = window.location.pathname;
const STELLAR_G = /^G[A-Z2-7]{55}$/;

let Page: React.ComponentType<any> = App;
let appProps: { initialProfileWallet?: string } = {};

if (path === '/map-preview') {
  Page = MapPreviewPage;
} else if (path.startsWith('/geoai/')) {
  // /geoai/<G…56>            → open the public profile
  // /geoai/<G…56>/<anything> → reserved for the AI-agent verbs that
  //   we'll layer on later (e.g. /geoai/<pk>/live, /geoai/<pk>/agent).
  //   For now we just take the first path segment as the wallet and
  //   let the rest become a tab hint someday.
  const segs = path.replace(/^\/geoai\//, '').split('/');
  const candidate = (segs[0] || '').trim();
  if (STELLAR_G.test(candidate)) {
    appProps.initialProfileWallet = candidate;
  }
  // Unknown-shape addresses fall through to App without a target —
  // the visitor sees the regular landing page instead of an error.
}

// The overlay coordinator wraps everything rather than sitting inside
// App, because the surfaces that were colliding render from different
// subtrees: the country hover card from App, the spatial cards from
// SpatialObjectsMapLayer. A provider below their common ancestor could
// not see both, which is how they came to overlap in the first place.
root.render(
  <React.StrictMode>
    <MapOverlayProvider>
      <Page {...appProps} />
    </MapOverlayProvider>
  </React.StrictMode>
);
